"use client";

interface Props {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Die letzte Seite vor dem Nichts: `global-error` ersetzt das Root-Layout, also gibt es
 * hier **kein CSS** — weder Tailwind noch die Theme-Variablen. Deshalb Inline-Styles.
 *
 * Was Inline-Styles NICHT koennen, ist `@media` — und ohne das leuchtet die Seite im
 * Dunkelmodus weiss, waehrend die ganze Suite dunkel ist. Der `<style>`-Block unten ist
 * deshalb kein Stilbruch, sondern die einzige Form, die hier traegt. `color-scheme`
 * faerbt zusaetzlich Scrollbalken und Standard-Bedienelemente mit.
 *
 * Die Farben sind monochrom wie die Marke. Bis 2026-08-19 stand am Knopf `#0070f3` —
 * das Blau der Next.js-Vorlage, das einzige Nicht-Marken-Blau der Suite, sichtbar auf
 * jeder Absturzseite.
 */
export default function GlobalError({ error, reset }: Props) {
  return (
    <html lang="de">
      <head>
        <style>{`
          :root { color-scheme: light dark; }
          body { margin: 0; background: #ffffff; color: #0a0a0a; }
          .jw-ge-leise { color: #666666; }
          .jw-ge-knopf { background: #0a0a0a; color: #ffffff; }
          @media (prefers-color-scheme: dark) {
            body { background: #0a0a0a; color: #fafafa; }
            .jw-ge-leise { color: #a1a1a1; }
            .jw-ge-knopf { background: #fafafa; color: #0a0a0a; }
          }
        `}</style>
      </head>
      <body>
        <div style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily: "system-ui, sans-serif",
        }}>
          <div style={{ maxWidth: "400px", textAlign: "center" }}>
            <h1 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1rem" }}>
              Schwerwiegender Fehler
            </h1>
            <p className="jw-ge-leise" style={{ marginBottom: "1rem" }}>
              {/* Ausnahmetext nur im Dev-Server — Begruendung in `ErrorPage.tsx` (C-14). */}
              {(process.env.NODE_ENV === "development" && error.message) || "Die Anwendung konnte nicht geladen werden."}
            </p>
            {error.digest && (
              <p className="jw-ge-leise" style={{ fontSize: "0.75rem", marginBottom: "1rem" }}>
                Fehler-ID: {error.digest}
              </p>
            )}
            <button
              onClick={() => reset()}
              className="jw-ge-knopf"
              style={{
                padding: "8px 16px",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.875rem",
              }}
            >
              Neu laden
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
